import {
    Bank,
    Board,
    CommonBoard,
    GameStep,
    GAME_STEP_BOARD_CLEARED,
    GAME_STEP_DOUBLE_STONE_IN_PIT,
    GAME_STEP_LAST_STONE_IN_BANK,
    GAME_STEP_LAST_STONE_IN_EMPTY_PIT,
    MancalaGame,
    Pit,
} from "mancala.js";
import { Game } from "../models/Game";

// A played move: an entry of MancalaGame.history in a live game, or a MoveRecord of a saved game.
export interface PlayedMove {
    playerId: string;
    moveIndex: number;
    boardSnapshot: number[];
    gameSteps: GameStep[];
}

export type CaptureKind = "even" | "emptyPit";

export interface MoveDescription {
    // 1-based.
    number: number;
    playerId: string;
    // 1-6, counted from the player's own side.
    pit: number;
    extraTurn: boolean;
    capture: boolean;
    captureKind?: CaptureKind;
    // Stones taken from the opponent's pit by a capture.
    captured: number;
    // Stones the mover's store gained with this move.
    storeGain: number;
    gameOver: boolean;
}

const referenceBoard = new CommonBoard();

export function getInitialBoard(): number[] {
    return new CommonBoard().getStoneArray();
}

// Stones in every pit after the given move; move 0 is the start position.
export function getBoardAfterMove(moves: PlayedMove[], moveNumber: number): number[] {
    const n = Math.min(Math.max(moveNumber, 0), moves.length);
    return n === 0 ? getInitialBoard() : [...moves[n - 1].boardSnapshot];
}

export function describeMove(moves: PlayedMove[], moveNumber: number): MoveDescription {
    const move = moves[moveNumber - 1];
    if (!move) throw new Error(`No move ${moveNumber}, the game has ${moves.length} moves`);
    const before = getBoardAfterMove(moves, moveNumber - 1);
    const isPlayer1Move = move.moveIndex <= referenceBoard.player1PitStopIndex();
    const firstPitIndex = isPlayer1Move ? referenceBoard.player1PitStartIndex() : referenceBoard.player2PitStartIndex();
    const storeIndex = isPlayer1Move ? referenceBoard.player1BankIndex() : referenceBoard.player2BankIndex();
    const stepTypes = move.gameSteps.map((step) => step.type);
    return {
        number: moveNumber,
        playerId: move.playerId,
        pit: move.moveIndex - firstPitIndex + 1,
        extraTurn: stepTypes.includes(GAME_STEP_LAST_STONE_IN_BANK),
        capture: stepTypes.includes(GAME_STEP_LAST_STONE_IN_EMPTY_PIT) || stepTypes.includes(GAME_STEP_DOUBLE_STONE_IN_PIT),
        captureKind: getCaptureKind(move.gameSteps),
        captured: getCapturedStones(before, move.moveIndex, move.gameSteps),
        storeGain: move.boardSnapshot[storeIndex] - before[storeIndex],
        gameOver: stepTypes.includes(GAME_STEP_BOARD_CLEARED),
    };
}

// Stones a capture took from the opponent's pit: what was there plus what this move dropped in. 0 without a capture.
export function getCapturedStones(before: number[], pitIndex: number, gameSteps: GameStep[]): number {
    const path = getSowingPath(before, pitIndex);
    const capturedFrom = (index: number) => before[index] + path.filter((i) => i === index).length;
    for (const step of gameSteps) {
        if (step.type === GAME_STEP_DOUBLE_STONE_IN_PIT) return capturedFrom(step.data.pitIndex);
        if (step.type === GAME_STEP_LAST_STONE_IN_EMPTY_PIT) return capturedFrom(step.data.oppositeIndex);
    }
    return 0;
}

export function getCaptureKind(gameSteps: GameStep[]): CaptureKind | undefined {
    if (gameSteps.some((step) => step.type === GAME_STEP_DOUBLE_STONE_IN_PIT)) return "even";
    if (gameSteps.some((step) => step.type === GAME_STEP_LAST_STONE_IN_EMPTY_PIT)) return "emptyPit";
    return undefined;
}

// Board indexes the stones of the given pit are dropped into, in order, as Board.move sows them (see
// Board.getSowingPath): the opponent's store is skipped. The last index is where the last stone lands. Empty for an
// empty pit or a store.
export function getSowingPath(stones: number[], pitIndex: number): number[] {
    return referenceBoard.getSowingPath(pitIndex, stones[pitIndex] ?? 0);
}

// The game as it was after the given move, for showing or animating that position (e.g. with PitAnimator,
// whose last history entry is the move it animates). Only for display: the rebuilt game has no rules.
export function createGameAtMove(game: Game, moves: PlayedMove[], moveNumber: number): Game {
    const n = Math.min(Math.max(moveNumber, 0), moves.length);
    const stones = getBoardAfterMove(moves, n);
    const pits = stones.map((stoneCount, index) =>
        referenceBoard.checkPitTypeIsBankByIndex(index) ? new Bank(index, stoneCount) : new Pit(index, stoneCount));
    const { id, player1Id, player2Id } = game.mancalaGame;
    const nextMove = moves[n];
    const mancalaGame = new MancalaGame(
        id,
        new Board(referenceBoard.playerPitCount, referenceBoard.initialStoneCountInPits, pits),
        player1Id,
        player2Id,
        nextMove ? nextMove.playerId : game.mancalaGame.turnPlayerId,
        [],
        moves.slice(0, n).map((move) => ({ ...move, gameSteps: [...move.gameSteps] })),
        n === moves.length ? game.mancalaGame.state : "playing"
    );
    return { ...game, mancalaGame };
}

// A game showing the given stones, for displaying a board (e.g. a preview) when only the stone counts are known.
export function createGameFromBoard(params: {
    id: string, player1Id: string, player2Id: string, turnPlayerId: string, board: number[],
}): Game {
    const pits = params.board.map((stoneCount, index) =>
        referenceBoard.checkPitTypeIsBankByIndex(index) ? new Bank(index, stoneCount) : new Pit(index, stoneCount));
    const mancalaGame = new MancalaGame(
        params.id,
        new Board(referenceBoard.playerPitCount, referenceBoard.initialStoneCountInPits, pits),
        params.player1Id,
        params.player2Id,
        params.turnPlayerId,
        [],
        []
    );
    return {
        id: params.id,
        mancalaGame,
        gameUsersConnectionInfo: {
            user1ConnectionInfo: { userId: params.player1Id, isOnline: false },
            user2ConnectionInfo: { userId: params.player2Id, isOnline: false },
        },
    };
}
